import Client from './services/client';
import Storage from './services/storage';
import { createAuthorizationHeader, createUrl } from './utils';

export default class SDK {
  constructor() {
    this.options = {
      accessTokenKey: 'neevo.access_token',
      baseUrl: window._env_.API_BASE_URL,
      refreshTokenKey: 'neevo.refresh_token'
    };

    this.storage = new Storage();
    this.client = new Client(this.options, this.storage);
  }

  buildUrl(uri, { queryParams, isSingularEndpoint } = {}) {
    let { baseUrl } = this.options;
    if (isSingularEndpoint && baseUrl.charAt(baseUrl.length - 1) === 's') {
      baseUrl = baseUrl.slice(0, -1);
    }
    return createUrl(uri, baseUrl, queryParams);
  }

  async api(
    uri,
    {
      requiresAuthentication,
      body,
      headers = {},
      method = 'get',
      queryParams,
      isSingularEndpoint
    } = {}
  ) {
    const url = this.buildUrl(uri, { queryParams, isSingularEndpoint });

    let request;

    if (requiresAuthentication && !headers.authorization) {
      const tokens = await this.getTokens();

      request = this.client.request(
        url,
        method,
        body,
        {
          ...createAuthorizationHeader(tokens.accessToken),
          ...headers
        },
        () => {
          return new Promise(resolve => {
            this.refreshTokens().then(result => {
              const token = result.body.accessToken;
              resolve({
                ...createAuthorizationHeader(token),
                ...headers
              });
            });
          });
        }
      );
    } else {
      request = this.client.request(url, method, body, headers);
    }

    const data = await request;

    return data;
  }

  async getTokens() {
    let refreshToken;

    const accessToken = await this.storage.getItem(this.options.accessTokenKey);

    if (accessToken === null) {
      throw new Error('InvalidAccessToken');
    }

    try {
      refreshToken = await this.storage.getItem(this.options.refreshTokenKey);

      if (refreshToken === null) {
        throw new Error('InvalidRefreshToken');
      }
    } catch (error) {
      return {
        accessToken
      };
    }

    return {
      accessToken,
      refreshToken
    };
  }

  removeToken() {
    return Promise.all([
      this.storage.removeItem(this.options.accessTokenKey),
      this.storage.removeItem(this.options.refreshTokenKey)
    ]);
  }

  async refreshTokens() {
    const { refreshToken } = await this.getTokens();

    try {
      const token = await this.api('/account/refresh', {
        body: {
          refreshToken
        },
        method: 'post'
      });

      await this.setToken({ token });

      return token;
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async setToken({ token }) {
    await this.storage.setItem(this.options.accessTokenKey, token.accessToken);

    if (token.refreshToken) {
      this.storage.setItem(this.options.refreshTokenKey, token.refreshToken);
    }

    return token;
  }

  async setItem(key, value) {
    try {
      await this.storage.setItem(key, JSON.stringify(value));

      return Promise.resolve();
    } catch (err) {
      return Promise.reject(err);
    }
  }

  async getItem(key) {
    const value = await this.storage.getItem(key);

    return JSON.parse(value);
  }
}
